import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { SummaryCard, HeadSquare, SummaryCardLineChart } from 'modules/common/SummaryCard';
import { SummaryCardRequestESG } from 'modules/ESG';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingConstants } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { generalInformation } from 'shared/constants/summaryTab';
import { useStyles, useHeadSquareStyles } from 'shared/styles/summaryCard';
import { fetchEntityBasic } from 'services';
import { summaryCardDocumentsParser, summaryCardHistoryParser } from 'shared/parsers';
import { useDispatch } from 'react-redux';
import { refreshEntityAction } from 'store/entity/entityActions';
import { RatingStatus } from 'shared/types/ratingStatus';
import { useFeatures } from 'shared/hooks';
import Typography from '@mui/material/Typography';
import ESGScoreOverview from './ESGScoreOverview/ESGScoreOverview';
import ESGImpactOverview from './ESGImpactOverview';
import ESGLendingImpact from './ESGLendingImpact/ESGLendingImpact';
import { ESGLearnMoreBar } from './ESGLearnMoreBar/ESGLearnMoreBar';
import { ESGMaterialityMap } from './ESGMaterialityMap';
import { ESGRelevanceMapping } from './ESGRelevanceMapping';
import {
  useCompanyVsValueChain,
  useGetImpactIndustryAbsolute,
  useGetImpactIndustryRelative,
  useGetImpactRegionAbsolute,
  useGetImpactRegionRelative,
  useMaterialityMap,
  useGetScoreOverview,
  useSDGRelevanceMapping,
  useImpactReview,
  usePortfolioCoverage,
} from './services/esgLendingService';
import { theme } from '../../theme';
import { getImpactOverview, getScoreOverview, getSummary } from './parsers';

export const ESG = ({ id, availability, industry, countryCode, selectedYear }) => {
  const { esgLendingData } = useFeatures();
  const classes = useStyles();
  const headSquareClasses = useHeadSquareStyles();
  const [entityData, setEntityData] = useState<{ isPending: boolean; data: any }>({
    isPending: true,
    data: null,
  });
  const { t } = useTranslation();
  const didUnmount = useRef(false);
  const dispatch = useDispatch();

  const { year } = selectedYear;

  const { data: companyVsValueChain } = useCompanyVsValueChain(id, year);
  const { data: impactIndustryAbsolute, loading: impactIndustryAbsoluteLoading } =
    useGetImpactIndustryAbsolute(id, year);
  const { data: impactIndustryRelative, loading: impactIndustryRelativeLoading } =
    useGetImpactIndustryRelative(id, year);
  const { data: impactRegionAbsolute, loading: impactRegionAbsoluteLoading } =
    useGetImpactRegionAbsolute(id, year);
  const { data: impactRegionRelative, loading: impactRegionRelativeLoading } =
    useGetImpactRegionRelative(id, year);
  const { data: materialityMap } = useMaterialityMap(id, year);
  const { data: scoreOverview, loading: scoreOverviewLoading } = useGetScoreOverview(id, year);
  const { data: relevanceMapping } = useSDGRelevanceMapping(id, year);
  const { data: impactReview, loading: impactReviewLoading } = useImpactReview(id, year);
  const { data: portfolioCoverage, loading: portfolioCoverageLoading } = usePortfolioCoverage(
    id,
    year,
  );

  const impactOverview = getImpactOverview(portfolioCoverage);
  const scoreOverviewData = getScoreOverview(scoreOverview);
  const esgScore = getSummary(impactReview);

  const lendingImpactLoading =
    impactIndustryAbsoluteLoading ||
    impactIndustryRelativeLoading ||
    impactRegionAbsoluteLoading ||
    impactRegionRelativeLoading;

  const { data_exists: dataExists, subscribed: isSubscribed, requestState } = availability || {};

  const isAvailable = !!esgScore;

  const showESGScoreSummaryCard = impactReviewLoading || (isAvailable && isSubscribed);

  const customScore = entityData?.data?.requestState === RatingStatus.DONE;

  const refreshEntity = () => {
    dispatch(refreshEntityAction());
  };

  const getEntityData = async () => {
    setEntityData((data) => ({ ...data, isPending: true }));

    try {
      const response = await fetchEntityBasic({
        id,
        ratingType: ratingTypes.ESG_IMPACT,
      });

      if (!didUnmount.current) {
        setEntityData(() => ({
          isPending: false,
          data: response.success ? response.data : null,
        }));
      }
    } catch (error) {
      if (!didUnmount.current) {
        setEntityData(() => ({ isPending: false, data: null }));
      }
    }
  };

  const getData = () => {
    if (dataExists) {
      getEntityData();
    }
    getScoreOverview();
  };

  const handleUnlocked = (unlocked) => {
    if (unlocked) refreshEntity();
  };

  const handleRequested = (requested) => {
    if (requested) refreshEntity();
  };

  useEffect(() => {
    if (id && selectedYear && availability) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedYear, availability]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  return (
    <>
      <Box mb={3}>
        {showESGScoreSummaryCard ? (
          <SummaryCard
            headSquare={
              <HeadSquare
                customScore={customScore}
                title={customScore ? '' : t('ESG.IMPACT_REVIEW_SCORE')}
                subtitle={customScore ? t('ESG.CUSTOM_SCORE_TOOLTIP') : ''}
              >
                <Typography
                  color={
                    generalInformation.includes('Unsolicited' && 'Without entity participation')
                      ? theme.palette.custom.orangeAccent
                      : ''
                  }
                >
                  <span className={headSquareClasses.score}>{esgScore?.score}</span>
                  <span className={headSquareClasses.max}>/10</span>
                </Typography>
              </HeadSquare>
            }
            history={
              <SummaryCardLineChart
                history={summaryCardHistoryParser(esgScore?.history)}
                isESGImpact
              />
            }
            className={classes.summaryCard}
            isLoading={impactReviewLoading}
            dataQuality={impactReview?.dataQuality}
            data={{
              ...esgScore,
              documents: summaryCardDocumentsParser(esgScore?.documents),
              generalInformationList: generalInformation,
              ratingScalesData: creditRatingScalesMapping(
                portfolioTypes.ESG_ANALYSIS,
                ratingConstants.entityReview,
              ),
            }}
          />
        ) : (
          <div className={classes.summaryCard}>
            <SummaryCardRequestESG
              scopeoneId={id}
              dataExists={dataExists}
              isSubscribed={isSubscribed}
              isAvailable={isAvailable}
              requestState={requestState}
              industry={industry}
              countryCode={countryCode}
              onRequested={handleRequested}
              onUnlocked={handleUnlocked}
            />
          </div>
        )}
      </Box>
      <ESGLearnMoreBar />
      {isAvailable && (
        <>
          {impactOverview && (
            <Box mb={3}>
              <ESGImpactOverview data={impactOverview} loading={portfolioCoverageLoading} />
            </Box>
          )}
          {relevanceMapping && (
            <Box mb={3}>
              <Paper>
                <ESGRelevanceMapping data={relevanceMapping} />
              </Paper>
            </Box>
          )}
          {scoreOverviewData && (
            <Box mb={3}>
              <ESGScoreOverview scores={scoreOverviewData} loading={scoreOverviewLoading} />
            </Box>
          )}
          {esgLendingData &&
            !!impactIndustryAbsolute &&
            !!impactIndustryRelative &&
            !!impactRegionAbsolute &&
            !!impactRegionRelative && (
              <Box my={3}>
                <Paper>
                  <ESGLendingImpact
                    industriesAbsolute={impactIndustryAbsolute.results}
                    industriesRelative={impactIndustryRelative.results}
                    countriesAbsolute={impactRegionAbsolute.results}
                    countriesRelative={impactRegionRelative.results}
                    loading={lendingImpactLoading}
                  />
                </Paper>
              </Box>
            )}
          {materialityMap && companyVsValueChain && (
            <Box mb={3}>
              <Paper>
                <ESGMaterialityMap mapData={materialityMap} chartData={companyVsValueChain} />
              </Paper>
            </Box>
          )}
        </>
      )}
    </>
  );
};

ESG.defaultProps = {
  entityName: undefined,
  industry: undefined,
  countryCode: undefined,
  availability: undefined,
  selectedYear: {},
};

ESG.propTypes = {
  id: PropTypes.string.isRequired,
  availability: PropTypes.shape({
    data_exists: PropTypes.bool,
    subscribed: PropTypes.bool,
    requestState: PropTypes.string,
  }),
  entityName: PropTypes.string,
  industry: PropTypes.string,
  countryCode: PropTypes.string,
  selectedYear: PropTypes.object,
};

export default ESG;
